import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, Maximize, Minimize, Home, Download } from 'lucide-react';

const CHURCH_NAME = 'CCC Valley of Berachah (VOB) Parish';
const CHURCH_NAME_FULL = 'Celestial Church of Christ — Valley of Berachah (VOB) Parish';
const BUILT_BY = 'Oluwakorede Akinsanya';
const DECK_VERSION = 'v1.1';
const DECK_UPDATED = 'February 2026';

const SLIDES = [
  {
    id: 1,
    type: 'title',
    title: CHURCH_NAME,
    subtitle: 'Church Management System & Website',
    desc: 'A comprehensive full-stack digital platform (cccvob.com) for managing every aspect of church operations — from membership to finance, rotas to choir, and the public-facing church website.',
    image: '/handbook/church.jpg',
    url: 'https://cccvob.com',
    gradient: 'from-slate-900/80 via-indigo-900/70 to-slate-900/80',
  },
  {
    id: 2,
    type: 'feature',
    badge: "WHAT'S NEW · v1.1",
    title: 'Version 1.1 — Highlights',
    points: [
      'Minister Availability Calendar (/minister-availability) with 7-day lockout',
      'Hybrid Choir Check-in — GPS fence for direct links, QR + venue token on-site',
      'Member Password Login (Phone + Password) with idempotent setup tokens',
      'Rota Email Ministers + automatic 3-day reminders with availability link',
      'A3 rota PDFs that auto-scale to always fit on one page',
      'Drag-and-drop Rules ordering · Split weekly-schedule slides · Compulsory Choir form fields',
      'Main-Admin-only Handbook download (server-side 403 for anyone else)',
    ],
    image: '/handbook/pres.jpg',
    gradient: 'from-amber-900/80 via-rose-900/60 to-amber-900/80',
  },
  {
    id: 3,
    type: 'feature',
    badge: 'PUBLIC WEBSITE',
    title: 'Church Homepage',
    points: [
      'Beautiful, mobile-responsive public homepage at cccvob.com',
      'Displays church announcements, service schedules, and upcoming events',
      'Lists active ministries with descriptions',
      'Dynamic inspirational flyer gallery',
      'Integrated member registration form — join the church online',
    ],
    image: '/handbook/worship.jpg',
    url: 'https://cccvob.com',
    gradient: 'from-blue-900/80 via-indigo-900/60 to-blue-900/80',
  },
  {
    id: 4,
    type: 'feature',
    badge: 'REGISTRATION · PASSWORD LOGIN',
    title: 'Member Registration & Password Login',
    points: [
      'Online registration via homepage form or QR code at church entrance',
      'Mandatory fields: Full Name, Phone (UK validated), Email, Birthday, Gender',
      'Homepage submissions go through admin Accept / Decline workflow',
      'Accepted members receive a branded email to set their own password',
      'Members then sign in with Phone + Password (no more surname-guessing)',
      'Pending Password Setup widget on the dashboard with Resend All action',
    ],
    image: '/handbook/register.jpg',
    url: 'https://cccvob.com/member-register',
    gradient: 'from-emerald-900/80 via-teal-900/60 to-emerald-900/80',
  },
  {
    id: 4,
    type: 'feature',
    badge: 'ADMIN DASHBOARD',
    title: 'Powerful Admin Panel',
    points: [
      'Role-based access: Main Admin, Sub-Admins, Secretariat',
      'Granular permissions per module (Members, Finance, Rota, Choir, etc.)',
      'Real-time analytics dashboard with attendance trends and financial summaries',
      'Audit logging for all critical actions',
      'Manage church settings, announcements, and content from one place',
    ],
    image: '/handbook/dashboard.jpg',
    url: 'https://cccvob.com/login',
    gradient: 'from-purple-900/80 via-violet-900/60 to-purple-900/80',
  },
  {
    id: 5,
    type: 'feature',
    badge: 'ROTA MANAGEMENT',
    title: 'Monthly Rota + Email Automation',
    points: [
      'Auto-generates monthly rotas for all service types (Morning, Evening, Midweek)',
      'Integrates Bible lessons from the CCC EC schedule',
      'Holy Week and special events auto-added with expiry dates',
      'Ministers marked unavailable show a red UNAVAILABLE badge in the rota',
      '"Email Ministers" sends the full rota + personal assignments by email',
      'Automated 3-day reminders with an Update My Availability link',
      'A3 PDF export with auto-scaling — always fits on a single page',
    ],
    image: '/handbook/rota.jpg',
    gradient: 'from-amber-900/80 via-orange-900/60 to-amber-900/80',
  },
  {
    id: 6,
    type: 'feature',
    badge: 'MINISTER AVAILABILITY',
    title: 'Minister Availability Calendar',
    points: [
      'Public page at cccvob.com/minister-availability',
      'Ministers toggle their own unavailable dates — no admin needed',
      '7-day lockout on saved dates prevents last-minute rota churn',
      'Rota UI highlights unavailable ministers with a red badge',
      'Availability link is embedded in every rota and 3-day reminder email',
    ],
    image: '/handbook/planning.jpg',
    url: 'https://cccvob.com/minister-availability',
    gradient: 'from-teal-900/80 via-emerald-900/60 to-teal-900/80',
  },
  {
    id: 6,
    type: 'feature',
    badge: 'HARVEST & FINANCE',
    title: 'Harvest Contributions & Finance',
    points: [
      'Record harvest contributions with unique receipt numbers',
      'Track multiple project types with per-project totals',
      'Generate downloadable/printable PDF & image receipts',
      'Void contributions with mandatory reasons and audit trail',
      'Disbursement tracking for church projects — all in British Pounds (£)',
    ],
    image: '/handbook/finance.jpg',
    url: 'https://cccvob.com/harvest-record',
    gradient: 'from-yellow-900/80 via-amber-900/60 to-yellow-900/80',
  },
  {
    id: 7,
    type: 'feature',
    badge: 'MOBILE HARVEST',
    title: 'Mobile Harvest Recording',
    points: [
      'Dedicated mobile-friendly page at cccvob.com/harvest-record',
      'Admins with finance permissions can record contributions on the go',
      'Accessed via QR code — no need to be at a computer',
      'Clickable project cards with real-time total calculations',
      'Receipts generated instantly for contributors',
    ],
    image: '/handbook/mobile.jpg',
    gradient: 'from-rose-900/80 via-pink-900/60 to-rose-900/80',
  },
  {
    id: 8,
    type: 'feature',
    badge: 'CHOIR MINISTRY',
    title: 'Choir & Music Ministry',
    points: [
      'Full choir member management with voice parts and instruments',
      'Digital VOB Choir form at cccvob.com/join-choir — every field compulsory',
      'Admin Accept / Decline workflow on the "VOB Form Applications" tab',
      'Members log in with Phone + Password (password set via secure email link)',
      'Song library + hymn selection with English/Yoruba split-screen display',
      'Password Status tab shows who has set up, pending, overdue',
    ],
    image: '/handbook/choir.jpg',
    url: 'https://cccvob.com/join-choir',
    gradient: 'from-fuchsia-900/80 via-purple-900/60 to-fuchsia-900/80',
  },
  {
    id: 9,
    type: 'feature',
    badge: 'CHOIR CHECK-IN · GPS + QR',
    title: 'Hybrid Attendance (GPS + Venue Token)',
    points: [
      'Self-service check-in at cccvob.com/choir-checkin',
      'Direct links enforce a 100m GPS fence around the church',
      'Admin-issued on-site QR attaches a venue token that bypasses GPS',
      'Scan method stored per row (gps / venue_token / manual)',
      'Upcoming-event lookup runs in Europe/London timezone (no midnight bugs)',
      'Auto "We missed you" emails after 2 consecutive absences',
    ],
    image: '/handbook/qr.jpg',
    url: 'https://cccvob.com/choir-checkin',
    gradient: 'from-purple-900/80 via-indigo-900/60 to-purple-900/80',
  },
  {
    id: 9,
    type: 'feature',
    badge: 'VOLUNTEERS',
    title: 'Volunteer Tracking System',
    points: [
      'QR code clock-in/clock-out for volunteers at cccvob.com/volunteer-clockin',
      'Tracks session hours per volunteer with date history',
      'Automated daily email reminders for unsigned-out volunteers',
      'Admin dashboard shows active sessions and historical reports',
      'Download volunteer reports as CSV',
    ],
    image: '/handbook/volunteer.jpg',
    gradient: 'from-cyan-900/80 via-teal-900/60 to-cyan-900/80',
  },
  {
    id: 10,
    type: 'feature',
    badge: 'RULES & TENETS',
    title: 'Rules & Tenets Presentation',
    points: [
      'Full-screen presentation mode for projecting church rules during services',
      'Sections reordered by drag-and-drop — new order reflects instantly',
      'Each weekly service day rendered on its own full-screen slide',
      'Supports background images and Bible passages per rule',
      'Auto-play with configurable timing or manual slide control',
      'Phone-based remote control via 6-digit code or QR',
    ],
    image: '/handbook/bible.jpg',
    url: 'https://cccvob.com/rules-presentation',
    gradient: 'from-indigo-900/80 via-blue-900/60 to-indigo-900/80',
  },
  {
    id: 11,
    type: 'feature',
    badge: 'COMMUNICATIONS',
    title: 'Email & Notifications',
    points: [
      'Branded email templates with VOB Church logo',
      'Send targeted emails to departments, ministries, or all members',
      'Automated volunteer sign-out reminders',
      'Void contribution notifications with reasons to contributors',
      'All emails delivered via secure IONOS SMTP',
    ],
    image: '/handbook/email.jpg',
    gradient: 'from-sky-900/80 via-blue-900/60 to-sky-900/80',
  },
  {
    id: 12,
    type: 'title',
    title: 'Key Links',
    subtitle: 'Quick Access to All Platform URLs',
    desc: '',
    links: [
      { label: 'Homepage', url: 'https://cccvob.com' },
      { label: 'Admin Login', url: 'https://cccvob.com/login' },
      { label: 'Member Registration', url: 'https://cccvob.com/member-register' },
      { label: 'Harvest Recording', url: 'https://cccvob.com/harvest-record' },
      { label: 'Join Choir', url: 'https://cccvob.com/join-choir' },
      { label: 'Choir Check-in', url: 'https://cccvob.com/choir-checkin' },
      { label: 'Volunteer Clock-in', url: 'https://cccvob.com/volunteer-clockin' },
      { label: 'Minister Availability', url: 'https://cccvob.com/minister-availability' },
      { label: 'Rules Presentation', url: 'https://cccvob.com/rules-presentation' },
    ],
    image: '/handbook/team.jpg',
    gradient: 'from-slate-900/80 via-indigo-900/70 to-slate-900/80',
  },
  {
    id: 13,
    type: 'closing',
    title: 'Thank You',
    subtitle: CHURCH_NAME_FULL,
    desc: '"For where two or three are gathered together in my name, there am I in the midst of them." — Matthew 18:20',
    image: '/handbook/church.jpg',
    gradient: 'from-slate-900/70 via-indigo-900/60 to-slate-900/70',
  },
];

const PlatformPresentation = ({ embedded = false }) => {
  const [current, setCurrent] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Auto-version pill — fetched from backend (reads /app/memory/PRD.md).
  // Falls back to hard-coded constants if the backend is unreachable.
  const [versionInfo, setVersionInfo] = useState({
    version: DECK_VERSION,
    updated: DECK_UPDATED,
  });
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backend}/api/handbook/version`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.version) setVersionInfo({ version: d.version, updated: d.updated }); })
      .catch(() => {});
  }, []);

  const goNext = useCallback(() => setCurrent(p => (p < SLIDES.length - 1 ? p + 1 : 0)), []);
  const goPrev = useCallback(() => setCurrent(p => (p > 0 ? p - 1 : SLIDES.length - 1)), []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      if (e.key === 'Escape') setAutoPlay(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goNext, goPrev]);

  useEffect(() => {
    if (!autoPlay) return;
    const timer = setInterval(goNext, 6000);
    return () => clearInterval(timer);
  }, [autoPlay, goNext]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Download available to Main Admin OR any admin with the `secretary`
  // permission (same gate as the Secretarial & Admin section that embeds this).
  // Show the download button to any logged-in admin; backend enforces the
  // actual permission check (Main Admin or Secretariat).
  const canDownloadDeck = (() => {
    if (typeof window === 'undefined') return false;
    const candidates = ['adminToken', 'admin_token', 'v2.1.0.church.adminToken'];
    for (const k of candidates) {
      if (localStorage.getItem(k)) return true;
    }
    for (const k of Object.keys(localStorage)) {
      if (/admin.?token/i.test(k) && localStorage.getItem(k)) return true;
    }
    return false;
  })();

  const handleDownload = async () => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const token =
        (typeof window !== 'undefined' &&
          (localStorage.getItem('adminToken') || localStorage.getItem('admin_token'))) || '';
      const res = await fetch(`${backend}/api/presentation/download`, {
        headers: token ? { 'x-admin-token': token } : {},
      });
      if (res.status === 401) { alert('Admin sign-in required to download the presentation.'); return; }
      if (res.status === 403) { alert('Only Main Admin or Secretariat can download the presentation.'); return; }
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CCC-VOB-Parish-Presentation.pptx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to download presentation. Please try again.');
    }
  };

  const slide = SLIDES[current];

  return (
    <div className={`${embedded ? 'h-full w-full' : 'h-screen w-screen'} bg-black relative overflow-hidden select-none`} data-testid="platform-presentation">
      {/* Background Image */}
      <AnimatePresence mode="wait">
        <motion.div key={`bg-${current}`}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0">
          <img src={slide.image} alt="" className="w-full h-full object-cover" />
          <div className={`absolute inset-0 bg-gradient-to-r ${slide.gradient}`} />
        </motion.div>
      </AnimatePresence>

      {/* BIG Logo Watermark — right side of every slide */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-end pr-8 md:pr-16 lg:pr-20 z-[5]">
        <img
          src="/church-logo.png?v=2"
          alt=""
          aria-hidden="true"
          className="object-contain"
          style={{
            width: 'min(55vh, 45vw)',
            height: 'min(55vh, 45vw)',
            opacity: 0.12,
            filter: 'drop-shadow(0 0 30px rgba(251, 191, 36, 0.25))',
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>

      {/* Version stamp — top-right */}
      <div
        className="absolute top-3 right-3 z-20 px-3 py-1 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-100 text-[10px] sm:text-xs font-semibold backdrop-blur flex items-center gap-1.5 pointer-events-none"
        data-testid="presentation-version-pill"
        title="Auto-updates from PRD.md every time a new CHANGELOG entry is committed"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
        {versionInfo.version} · updated {versionInfo.updated}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center px-8 md:px-16 lg:px-24">
        <AnimatePresence mode="wait">
          <motion.div key={`content-${current}`}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl">

            {slide.type === 'title' && (
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-4 leading-tight tracking-tight">{slide.title}</h1>
                <p className="text-xl sm:text-2xl text-blue-200 font-light mb-6">{slide.subtitle}</p>
                {slide.desc && <p className="text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">{slide.desc}</p>}
                {slide.url && <p className="mt-6 text-indigo-300 font-mono text-sm">{slide.url}</p>}
                {slide.links && (
                  <div className="grid sm:grid-cols-2 gap-3 mt-8 max-w-xl mx-auto text-left">
                    {slide.links.map(l => (
                      <div key={l.label} className="bg-white/10 backdrop-blur rounded-xl px-4 py-3 border border-white/20">
                        <p className="text-white font-semibold text-sm">{l.label}</p>
                        <p className="text-blue-300 text-xs font-mono">{l.url}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {slide.type === 'feature' && (
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-bold tracking-widest mb-4 border border-white/20">
                  {slide.badge}
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">{slide.title}</h2>
                <ul className="space-y-3 mb-6">
                  {slide.points.map((p, i) => (
                    <motion.li key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + i * 0.1 }}
                      className="flex items-start gap-3 text-slate-200 text-base sm:text-lg">
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
                      <span>{p}</span>
                    </motion.li>
                  ))}
                </ul>
                {slide.url && (
                  <p className="text-indigo-300 font-mono text-sm mt-4">{slide.url}</p>
                )}
              </div>
            )}

            {slide.type === 'closing' && (
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-4">{slide.title}</h1>
                <p className="text-xl text-blue-200 font-light mb-8">{slide.subtitle}</p>
                <p className="text-base text-slate-300 italic max-w-xl mx-auto">{slide.desc}</p>
                <p className="mt-8 text-indigo-300 font-mono text-sm">cccvob.com</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="absolute bottom-0 left-0 right-0 z-20">
        <div className="flex items-center justify-between px-6 py-4">
          {!embedded && (
            <a href="/" className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur">
              <Home className="w-5 h-5" />
            </a>
          )}
          {embedded && <div />}
          <div className="flex items-center gap-3">
            <button onClick={goPrev} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur" data-testid="slide-prev">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-1.5">
              {SLIDES.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                  className={`h-1.5 rounded-full transition-all ${i === current ? 'w-8 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/60'}`} />
              ))}
            </div>
            <button onClick={goNext} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur" data-testid="slide-next">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {canDownloadDeck && (
              <button onClick={handleDownload}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur"
                data-testid="slide-download"
                title="Download presentation as PowerPoint (.pptx)">
                <Download className="w-5 h-5" />
              </button>
            )}
            <button onClick={() => setAutoPlay(!autoPlay)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur" data-testid="slide-autoplay">
              {autoPlay ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            <button onClick={toggleFullscreen}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur">
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
            <span className="text-white/60 text-xs font-mono ml-2">{current + 1}/{SLIDES.length}</span>
          </div>
        </div>
      </div>

      {/* On-screen "Built by" signature — visible on every slide */}
      <div className="absolute bottom-20 left-6 md:left-12 z-20 pointer-events-none">
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-0.5">Built by</p>
        <p className="text-base md:text-lg text-indigo-200" style={{ fontFamily: '"Brush Script MT", cursive' }}>{BUILT_BY}</p>
      </div>
    </div>
  );
};

export default PlatformPresentation;
